import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./RangeSlider.module.scss";
import { RangeSliderProps } from "./types";

const RangeSlider: React.FC<RangeSliderProps> = ({
  label = "",
  min = 0,
  max = 100,
  defaultValue = 0,
  value: externalValue,
  showValue = true,
  onChange,
  ariaLabel,
  valuePrefix = "",
  valueSuffix = "",
  step = 1,
  disabled = false,
  register,
  editableInput = false,
  field = "page_max_override",
  className,
  valueFormat = "number",
  scaleStops,
}) => {
  const isControlled = externalValue !== undefined;

  // Piecewise-linear scale: the native range input runs in "track units"
  // (SEGMENT_UNITS per stop pair) and the mapping functions translate
  // between track position and value. Without scaleStops both spaces are
  // identical and the mappings are pass-throughs.
  const stops = scaleStops && scaleStops.length >= 2 ? scaleStops : null;
  const SEGMENT_UNITS = 100;
  const rangeMin = stops ? stops[0] : min;
  const rangeMax = stops ? stops[stops.length - 1] : max;
  const trackMax = stops ? (stops.length - 1) * SEGMENT_UNITS : max;

  const valueToTrack = (v: number): number => {
    if (!stops) return v;
    const clamped = Math.min(Math.max(v, rangeMin), rangeMax);
    let segment = stops.findIndex(
      (_, i) => i < stops.length - 1 && clamped <= stops[i + 1],
    );
    if (segment === -1) segment = stops.length - 2;
    const lo = stops[segment];
    const hi = stops[segment + 1];

    return (
      segment * SEGMENT_UNITS + ((clamped - lo) / (hi - lo)) * SEGMENT_UNITS
    );
  };

  const trackToValue = (pos: number): number => {
    if (!stops) return pos;
    const segment = Math.min(Math.floor(pos / SEGMENT_UNITS), stops.length - 2);
    const lo = stops[segment];
    const hi = stops[segment + 1];
    const step = (hi - lo) / SEGMENT_UNITS;
    const offset =
      ((pos - segment * SEGMENT_UNITS) / SEGMENT_UNITS) * (hi - lo);

    // Snap the offset within the segment so stop values round-trip exactly
    return lo + Math.round(offset / step) * step;
  };

  const [internalValue, setInternalValue] = useState(
    isControlled ? externalValue : defaultValue,
  );

  useEffect(() => {
    if (isControlled && externalValue !== internalValue) {
      setInternalValue(externalValue);
      updateProgress(externalValue);
    }
  }, [externalValue]);

  const progressRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tempInputValue, setTempInputValue] = useState("");
  // Value captured when editing starts, used to revert invalid input on blur
  const valueOnFocusRef = useRef<number>(defaultValue);

  const currentValue = isControlled ? externalValue : internalValue;
  const displayValue = currentValue;

  const updateProgress = useCallback(
    (newValue: number) => {
      if (progressRef.current) {
        const percentage = stops
          ? (valueToTrack(newValue) / trackMax) * 100
          : ((newValue - min) / (max - min)) * 100;
        progressRef.current.style.width = `${Math.max(0, Math.min(100, percentage))}%`;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [min, max, stops, trackMax],
  );
  useEffect(() => {
    updateProgress(currentValue);
  }, []);

  useEffect(() => {
    updateProgress(currentValue);
  }, [currentValue, updateProgress]);

  const commitValue = useCallback(
    (newValue: number) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }

      updateProgress(newValue);
      onChange?.(newValue);
    },
    [isControlled, onChange, updateProgress],
  );

  // Parse raw input, returning the number only if it is finite and in range
  const parseInRange = useCallback(
    (raw: string): number | null => {
      const parsed = Number(raw);
      const isValid =
        raw !== "" &&
        Number.isFinite(parsed) &&
        parsed >= rangeMin &&
        parsed <= rangeMax;

      return isValid ? parsed : null;
    },
    [rangeMin, rangeMax],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const position = parseInt(event.target.value);
      commitValue(stops ? trackToValue(position) : position);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commitValue, stops],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editableInput) {
      const raw = e.target.value.replace(/,/g, "");
      setTempInputValue(raw);

      // Only commit a live update for a valid in-range number; invalid input
      // (text, negatives, out of range) is reverted on blur instead.
      const valid = parseInRange(raw);
      if (valid !== null) {
        commitValue(valid);
      }
    }
  };

  const handleInputFocus = () => {
    if (editableInput) {
      setIsEditing(true);
      valueOnFocusRef.current = currentValue;
      setTempInputValue(String(currentValue));
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);

    // Revert to the value the field held when editing started on invalid input
    if (parseInRange(tempInputValue.replace(/,/g, "")) === null) {
      commitValue(valueOnFocusRef.current);
    }
  };

  const getInputValue = () => {
    if (isEditing) {
      return tempInputValue;
    }

    return displayValue;
  };

  const value =
    valueFormat === "percent"
      ? `${getInputValue()}%`
      : valueFormat === "comma" && !isEditing
        ? Number(displayValue).toLocaleString()
        : getInputValue();

  return (
    <div
      // @ts-expect-error TS2551 non-existing className
      className={clsx(styles["range-slider"], className)}
      role="group"
      aria-labelledby={label ? "range-slider-label" : undefined}
    >
      {label && (
        <label
          id="range-slider-label"
          // @ts-expect-error TS2551 non-existing className
          className={styles["range-slider__label"]}
        >
          {label}
        </label>
      )}

      {showValue && (
        <input
          type={valueFormat === "number" ? "number" : "text"}
          readOnly={!editableInput}
          // @ts-expect-error TS2551 non-existing className
          className={styles["range-slider__value"]}
          value={value}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          disabled={disabled}
          min={rangeMin}
          max={rangeMax}
          aria-label={`Current value: ${valuePrefix}${displayValue}${valueSuffix}`}
        />
      )}

      {/*
       // @ts-expect-error TS2551 non-existing className */}
      <div className={styles["range-slider__control"]}>
        {/*
         // @ts-expect-error TS2551 non-existing className */}
        <div className={styles["range-slider__track"]}>
          {/*
           // @ts-expect-error TS2551 non-existing className */}
          <div ref={progressRef} className={styles["range-slider__progress"]} />
        </div>

        <input
          type="range"
          // @ts-expect-error TS2551 non-existing className
          className={styles["range-slider__input"]}
          min={stops ? 0 : min}
          max={trackMax}
          step={stops ? 1 : step}
          value={stops ? valueToTrack(currentValue) : currentValue}
          onChange={handleChange}
          disabled={disabled}
          aria-label={
            ariaLabel ||
            `Range slider, current value: ${valuePrefix}${displayValue}${valueSuffix}`
          }
          aria-valuemin={rangeMin}
          aria-valuemax={rangeMax}
          aria-valuenow={currentValue}
          {...(!editableInput ? register?.(field) : {})}
        />
      </div>
    </div>
  );
};

export default RangeSlider;
